import { useState, useCallback, useEffect } from 'react'
import { adminLogin, getAdminStats, getRecentRequests, getProviders, getServerHealth, getDevices, getDeviceLog } from '../utils/api'
import type { DeviceRow, DeviceEvent } from '../utils/api'
import styles from './AdminPage.module.css'

interface Stats {
  total_requests: number
  total_success: number
  total_errors: number
  errorBreakdown: Array<{ error: string; count: number }>
  providerBreakdown: Array<{ provider: string; count: number }>
}

interface RequestLog {
  id: number
  url: string
  ip: string
  success: number
  error: string | null
  provider: string
  created_at: string
}

// Human-readable device summary from a raw User-Agent string.
function parseUA(ua: string): string {
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : /wv|WebView/.test(ua) ? 'App WebView'
    : ua ? 'Unknown' : '—'
  const os = /Windows/.test(ua) ? 'Windows'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad|iPod/.test(ua) ? 'iOS'
    : /Mac OS/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : ''
  return os ? `${browser} · ${os}` : browser
}

export function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') || '')
  const [loginData, setLoginData] = useState({ username: '', password: '' })
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [stats, setStats] = useState<Stats | null>(null)
  const [requests, setRequests] = useState<RequestLog[]>([])
  const [providers, setProviders] = useState<Array<{name: string; enabled: boolean}>>([])
  const [health, setHealth] = useState<any>(null)
  const [devices, setDevices] = useState<DeviceRow[]>([])
  const [deviceLog, setDeviceLog] = useState<DeviceEvent[] | null>(null)
  const [selectedDevice, setSelectedDevice] = useState('')
  const [deviceLoading, setDeviceLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'stats' | 'requests' | 'devices' | 'providers' | 'health'>('stats')
  const [dataLoading, setDataLoading] = useState(false)

  const loadData = useCallback(async (t: string) => {
    if (!t) return
    setDataLoading(true)
    try {
      const [s, r, p, h, d] = await Promise.all([
        getAdminStats(t),
        getRecentRequests(t, 50),
        getProviders(t),
        getServerHealth(t),
        getDevices(t)
      ])
      setStats(s)
      setRequests(r)
      setProviders(p)
      setHealth(h)
      setDevices(Array.isArray(d) ? d : [])
    } catch (err: any) {
      if (err.response?.status === 401) {
        setToken('')
        sessionStorage.removeItem('admin_token')
      }
    }
    setDataLoading(false)
  }, [])

  useEffect(() => {
    if (token) loadData(token)
  }, [token])

  const openDevice = useCallback(async (id: string) => {
    setSelectedDevice(id)
    setDeviceLoading(true)
    setDeviceLog(null)
    try {
      const log = await getDeviceLog(token, id)
      setDeviceLog(Array.isArray(log) ? log : [])
    } catch {
      setDeviceLog([])
    }
    setDeviceLoading(false)
  }, [token])

  const handleLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    setLoginLoading(true)
    try {
      const res = await adminLogin(loginData.username, loginData.password)
      setToken(res.token)
      sessionStorage.setItem('admin_token', res.token)
    } catch (err: any) {
      setLoginError(err.response?.data?.error || 'Login failed')
    }
    setLoginLoading(false)
  }, [loginData])

  const handleLogout = useCallback(() => {
    setToken('')
    sessionStorage.removeItem('admin_token')
    setStats(null)
    setRequests([])
    setDevices([])
    setDeviceLog(null)
    setSelectedDevice('')
  }, [])

  if (!token) {
    return (
      <div className={styles.loginPage}>
        <div className={styles.loginCard}>
          <div className={styles.loginIcon}>🔒</div>
          <h1 className={styles.loginTitle}>Admin Panel</h1>
          <p className={styles.loginDesc}>Akash Player</p>
          <form className={styles.loginForm} onSubmit={handleLogin} id="admin-login-form">
            <input
              id="admin-username"
              type="text"
              className={styles.loginInput}
              placeholder="Username"
              value={loginData.username}
              onChange={e => setLoginData(p => ({...p, username: e.target.value}))}
              autoComplete="username"
              required
            />
            <input
              id="admin-password"
              type="password"
              className={styles.loginInput}
              placeholder="Password"
              value={loginData.password}
              onChange={e => setLoginData(p => ({...p, password: e.target.value}))}
              autoComplete="current-password"
              required
            />
            {loginError && <p className={styles.loginError}>{loginError}</p>}
            <button
              id="admin-login-btn"
              type="submit"
              className={styles.loginBtn}
              disabled={loginLoading}
            >
              {loginLoading ? 'Logging in…' : 'Login'}
            </button>
          </form>
          <p className={styles.loginNote}>
            Username: <code>akash</code> · Password: <code>1</code>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.bgGlow} aria-hidden="true" />

      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Admin Dashboard</h1>
            <p className={styles.subtitle}>Akash Player</p>
          </div>
          <button id="admin-logout" className={styles.logoutBtn} onClick={handleLogout}>
            Logout
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statIcon}>📊</span>
              <span className={styles.statValue}>{stats.total_requests.toLocaleString()}</span>
              <span className={styles.statLabel}>Total Requests</span>
            </div>
            <div className={`${styles.statCard} ${styles.success}`}>
              <span className={styles.statIcon}>✅</span>
              <span className={styles.statValue}>{stats.total_success.toLocaleString()}</span>
              <span className={styles.statLabel}>Successful</span>
            </div>
            <div className={`${styles.statCard} ${styles.error}`}>
              <span className={styles.statIcon}>❌</span>
              <span className={styles.statValue}>{stats.total_errors.toLocaleString()}</span>
              <span className={styles.statLabel}>Errors</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statIcon}>📈</span>
              <span className={styles.statValue}>
                {stats.total_requests > 0
                  ? `${Math.round((stats.total_success / stats.total_requests) * 100)}%`
                  : '—'}
              </span>
              <span className={styles.statLabel}>Success Rate</span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className={styles.tabs}>
          {(['stats', 'requests', 'devices', 'providers', 'health'] as const).map(tab => (
            <button
              key={tab}
              id={`admin-tab-${tab}`}
              className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
          <button className={styles.refreshBtn} onClick={() => loadData(token)} disabled={dataLoading}>
            {dataLoading ? '⟳' : '↻ Refresh'}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'stats' && stats && (
          <div className={styles.tabContent}>
            <div className={styles.breakdownGrid}>
              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>Provider Breakdown</h3>
                {stats.providerBreakdown.map(p => (
                  <div key={p.provider} className={styles.breakdownRow}>
                    <span className={styles.breakdownName}>{p.provider}</span>
                    <span className={styles.breakdownCount}>{p.count}</span>
                  </div>
                ))}
                {stats.providerBreakdown.length === 0 && (
                  <p className={styles.empty}>No data yet</p>
                )}
              </div>
              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>Error Breakdown</h3>
                {stats.errorBreakdown.map(e => (
                  <div key={e.error} className={styles.breakdownRow}>
                    <span className={styles.breakdownName}>{e.error}</span>
                    <span className={`${styles.breakdownCount} ${styles.errorCount}`}>{e.count}</span>
                  </div>
                ))}
                {stats.errorBreakdown.length === 0 && (
                  <p className={styles.empty}>No errors recorded</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'requests' && (
          <div className={styles.tabContent}>
            <div className={styles.requestsTable}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>URL</th>
                    <th>Provider</th>
                    <th>Status</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r.id}>
                      <td className={styles.tdTime}>{new Date(r.created_at).toLocaleTimeString()}</td>
                      <td className={styles.tdUrl} title={r.url}>
                        {r.url.length > 50 ? r.url.slice(0, 50) + '…' : r.url}
                      </td>
                      <td><span className={styles.providerBadge}>{r.provider}</span></td>
                      <td>
                        <span className={r.success ? styles.successBadge : styles.errorBadge}>
                          {r.success ? '✓' : '✗'}
                        </span>
                      </td>
                      <td className={styles.tdError}>{r.error || '—'}</td>
                    </tr>
                  ))}
                  {requests.length === 0 && (
                    <tr><td colSpan={5} className={styles.empty}>No requests yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Devices — which device pasted which links, where, and what it played */}
        {activeTab === 'devices' && (
          <div className={styles.tabContent}>
            {!selectedDevice ? (
              <div className={styles.requestsTable}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Browser / OS</th>
                      <th>Location</th>
                      <th>IP</th>
                      <th>Links</th>
                      <th>Played</th>
                      <th>Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map(d => (
                      <tr
                        key={d.device_id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => openDevice(d.device_id)}
                      >
                        <td title={d.device_id}>{d.device_id.slice(0, 8)}…</td>
                        <td>{parseUA(d.ua)}</td>
                        <td>{[d.city, d.country].filter(Boolean).join(', ') || '—'}</td>
                        <td className={styles.tdUrl}>{d.ip || '—'}</td>
                        <td>{d.total}</td>
                        <td><span className={styles.successBadge}>{d.plays} ✓</span></td>
                        <td className={styles.tdTime}>{new Date(d.last_seen).toLocaleString()}</td>
                      </tr>
                    ))}
                    {devices.length === 0 && (
                      <tr>
                        <td colSpan={7} className={styles.empty}>
                          No devices yet — paste a link to start tracking
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div>
                <button
                  className={styles.refreshBtn}
                  onClick={() => { setSelectedDevice(''); setDeviceLog(null) }}
                >
                  ← All Devices
                </button>
                <p className={styles.empty}>
                  Device <strong title={selectedDevice}>{selectedDevice.slice(0, 12)}…</strong> — link history
                </p>
                {deviceLoading && <p className={styles.empty}>Loading…</p>}
                {!deviceLoading && (
                  <div className={styles.requestsTable}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Video URL</th>
                          <th>Provider</th>
                          <th>Status</th>
                          <th>Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(deviceLog || []).map(ev => (
                          <tr key={ev.id}>
                            <td className={styles.tdTime}>{new Date(ev.ts).toLocaleString()}</td>
                            <td className={styles.tdUrl} title={ev.url}>
                              {ev.url.length > 50 ? ev.url.slice(0, 50) + '…' : ev.url}
                            </td>
                            <td><span className={styles.providerBadge}>{ev.provider || '—'}</span></td>
                            <td>
                              <span className={ev.success ? styles.successBadge : styles.errorBadge}>
                                {ev.success ? '✓' : '✗'}
                              </span>
                            </td>
                            <td className={styles.tdError}>
                              {[ev.city, ev.country].filter(Boolean).join(', ') || '—'}
                            </td>
                          </tr>
                        ))}
                        {(deviceLog || []).length === 0 && (
                          <tr><td colSpan={5} className={styles.empty}>No events</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'providers' && (
          <div className={styles.tabContent}>
            <div className={styles.providersGrid}>
              {providers.map(p => (
                <div key={p.name} className={styles.providerCard}>
                  <div className={styles.providerName}>{p.name}</div>
                  <div className={`${styles.providerStatus} ${p.enabled ? styles.enabled : styles.disabled}`}>
                    {p.enabled ? '● Active' : '○ Disabled'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'health' && health && (
          <div className={styles.tabContent}>
            <div className={styles.healthGrid}>
              <div className={styles.healthCard}>
                <span className={styles.healthLabel}>Status</span>
                <span className={`${styles.healthValue} ${styles.healthOk}`}>● {health.status}</span>
              </div>
              <div className={styles.healthCard}>
                <span className={styles.healthLabel}>Uptime</span>
                <span className={styles.healthValue}>{Math.floor(health.uptime / 60)}m {Math.floor(health.uptime % 60)}s</span>
              </div>
              <div className={styles.healthCard}>
                <span className={styles.healthLabel}>Database</span>
                <span className={`${styles.healthValue} ${health.db ? styles.healthOk : ''}`}>
                  {health.db ? '● D1 connected' : '○ No D1'}
                </span>
              </div>
              <div className={styles.healthCard}>
                <span className={styles.healthLabel}>Timestamp</span>
                <span className={styles.healthValue}>{health.timestamp ? new Date(health.timestamp).toLocaleTimeString() : '—'}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
